<template>
    <div class="modal-card" style="width:925px">
        <header class="modal-card-head">
            <p class="modal-card-title">Date Range Selection</p>
            <p is-pull-right>Data available from {{minDate | customDateFormat('DD-MMM-YYYY')}} to {{maxDate | customDateFormat('DD-MMM-YYYY')}}</p>
        </header>
        <section class="modal-card-body">
            <b-message type="is-info" has-icon icon-size="is-small">
                Please select the range of dates for which Conversability scores will be calculated.
                Be aware that available metrics may be limited based on when the Project was created.
            </b-message>

            <div class="date-selection">
                <div class="columns is-gapless is-vcentered">
                    <div class="column is-four-fifth">
                        <div class="columns is-include-validation">
                            <div class="column start-date is-half">
                                <b-field horizontal label="Start Date"
                                    :type = "inputFieldStartDateErrorMsg && inputFieldStartDateErrorMsg.length > 0 ? 'is-danger' : 'is-normal'"
                                    :message="inputFieldStartDateErrorMsg">
                                    <b-input placeholder="DD-MM-YYYY" v-model="inputFieldStartDate"></b-input>
                                </b-field>
                            </div>
                            <div class="column end-date is-half">
                                <b-field horizontal label="End Date"
                                    :type = "inputFieldEndDateErrorMsg && inputFieldEndDateErrorMsg.length > 0 ? 'is-danger' : 'is-normal'"
                                    :message="inputFieldEndDateErrorMsg">
                                    <b-input placeholder="DD-MM-YYYY" v-model="inputFieldEndDate"></b-input>
                                </b-field>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column start-date is-half">
                                <b-field>
                                    <b-datepicker inline
                                        v-model="startDate"
                                        :max-date="endDate"
                                        :min-date="minDate"
                                        :events="highlightableDates"
                                        indicators="none"
                                        @input="selectDateManually">
                                    </b-datepicker>
                                </b-field>
                            </div>
                            <div class="column end-date is-half">
                                <b-field>
                                    <b-datepicker inline
                                        v-model="endDate"
                                        :min-date="startDate"
                                        :max-date="maxDate"
                                        :events="highlightableDates"
                                        indicators="none"
                                        @input="selectDateManually">
                                    </b-datepicker>
                                </b-field>
                            </div>
                        </div>
                    </div>
                    <div class="column is-one-fifth">
                        <div class="quick-selections buttons">
                            <b-button v-for="item in dateQuickSelections" :key="item.type.key" class="quick-selection-btn"
                            :type="selectedQuickSelection.type.key === item.type.key ? 'is-primary' : 'is-text'"
                            size="is-small" @click="selectQuickSelection(item)"
                            :disabled="!item.enabled"
                            :title="quickSelectionTooltips(item)">
                                {{item.type.name}}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <footer class="modal-card-foot">
            <button type="button" class="button" @click="close">Close</button>
            <button type="button" class="button is-primary" @click="apply">Apply Selected Dates</button>
        </footer>
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { dateRangeSelectionService } from '@/services/date-range-selection-service';
import constants from '@/constants.js';

const inputFieldDateFormat = 'DD-MMM-YYYY';

export default {
    name: 'DateRangeSelection',
    props: {
        dates: {
            type: Array,
            required: true
        },
        selectedDateRange: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            isLoading: true,
            startDate: new Date(),
            endDate: new Date(),
            minDate: new Date(),
            maxDate: new Date(),
            highlightableDates: [],
            inputFieldStartDate: moment().format(inputFieldDateFormat),
            inputFieldEndDate: moment().format(inputFieldDateFormat),
            inputFieldStartDateErrorMsg: '',
            inputFieldEndDateErrorMsg: ''
        };
    },
    created () {
        this.isLoading = false;
        if (this.dates && this.dates.length > 0) {
            this.minDate = _.minBy(this.dates, d => d.date).date;
            this.maxDate = _.maxBy(this.dates, d => d.date).date;
            this.dateQuickSelections = dateRangeSelectionService.buildDateQuickSelections(this.minDate, this.maxDate);

            let selectedQuickSelection = dateRangeSelectionService.getDefaultQuickSelection(this.dateQuickSelections);

            if (this.selectedDateRange && this.selectedDateRange.type) {
                let quickSelection = _.find(this.dateQuickSelections, qs => qs.type.key === this.selectedDateRange.type.key);
                if (quickSelection && quickSelection.enabled) {
                    // If it is a custom selection then set the given start date and end date.
                    if (quickSelection.type.key === constants.DATE_QUICK_SELECTIONS.CUSTOM.key) {
                        quickSelection.startDate = this.selectedDateRange.startDate;
                        quickSelection.endDate = this.selectedDateRange.endDate;
                    }
                    selectedQuickSelection = quickSelection;
                }
            }

            this.selectedQuickSelection = selectedQuickSelection;
            this.setStartEndDate(this.selectedQuickSelection);
        }
    },
    methods: {
        apply () {
            this.$emit('dateRangeSelectionApplied', buildDateRangeObject(this.selectedQuickSelection, this.startDate, this.endDate));
            this.$parent.close();
        },
        close () {
            this.$parent.close();
        },
        setStartEndDate (selectedQuickSelection) {
            if (selectedQuickSelection) {
                this.startDate = selectedQuickSelection.startDate;
                this.endDate = selectedQuickSelection.endDate;
            } else {
                this.startDate = this.minDate;
                this.endDate = this.maxDate;
            }
            this.inputFieldStartDate = moment(this.startDate).format(inputFieldDateFormat);
            this.inputFieldEndDate = moment(this.endDate).format(inputFieldDateFormat);
            this.buildHighlightableDates();
        },
        selectQuickSelection (selectedQuickSelection) {
            if (selectedQuickSelection.type.key !== constants.DATE_QUICK_SELECTIONS.CUSTOM.key) {
                this.selectedQuickSelection = selectedQuickSelection;
                this.setStartEndDate(selectedQuickSelection);
            }
        },
        buildHighlightableDates () {
            this.highlightableDates = getDatesBetween(this.startDate, this.endDate);
        },
        selectDateManually (date) {
            // If date is selected manually, we need to set the quick selection to custom.
            let quickSelection = this.dateQuickSelections.find(qs => qs.type.key === constants.DATE_QUICK_SELECTIONS.CUSTOM.key);
            if (quickSelection) {
                quickSelection.startDate = this.startDate;
                quickSelection.endDate = this.endDate;
                this.selectedQuickSelection = quickSelection;
                this.setStartEndDate(quickSelection);
            }
        },
        quickSelectionTooltips (quickSelection) {
            let prefix = !quickSelection.enabled ? 'Insufficient data from' : 'From';
            return `${prefix} ${moment(quickSelection.startDate).format('DD-MMM-YYYY')} to ${moment(quickSelection.endDate).format('DD-MMM-YYYY')}`;
        }
    },
    watch: {
        'inputFieldStartDate': function (newValue, oldValue) {
            let validatedDateObj = validateInputDate(newValue, this.minDate, this.endDate);
            if (!validatedDateObj.date && validatedDateObj.hasError && validatedDateObj.isValidDate) {
                this.inputFieldStartDateErrorMsg = 'Start Date must be before the End Date';
            } else {
                this.inputFieldStartDateErrorMsg = validatedDateObj.errorMessage;
                this.startDate = validatedDateObj.date;
                this.buildHighlightableDates();
            }
        },
        'inputFieldEndDate': function (newValue, oldValue) {
            let validatedDateObj = validateInputDate(newValue, this.startDate, this.maxDate);
            if (!validatedDateObj.date && validatedDateObj.hasError && validatedDateObj.isValidDate) {
                this.inputFieldEndDateErrorMsg = 'End Date must be after the Start Date';
            } else {
                this.inputFieldEndDateErrorMsg = validatedDateObj.errorMessage;
                this.endDate = validatedDateObj.date;
                this.buildHighlightableDates();
            }
        }
    }
};

const buildDateRangeObject = (quickSelection, startDate, endDate) => {
    if (quickSelection.type.key === constants.DATE_QUICK_SELECTIONS.CUSTOM.key) {
        quickSelection.startDate = startDate;
        quickSelection.endDate = endDate;
    }
    return quickSelection;
};

const validateInputDate = (date, minDate, maxDate) => {
    let parsedDate = moment(date, inputFieldDateFormat, true);
    if (!parsedDate.isValid()) {
        return {
            date: null,
            isValidDate: false,
            hasError: true,
            errorMessage: 'Invalid date'
        };
    }
    // Check if the date falls within the min and max date
    if (parsedDate.isBetween(moment(minDate), moment(maxDate), 'day', '[]')) {
        return {
            date: parsedDate.toDate(),
            isValidDate: true,
            hasError: false
        };
    } else {
        return {
            date: null,
            isValidDate: true,
            hasError: true,
            errorMessage: 'Date is outside the range'
        };
    }
};

const getDatesBetween = (startDate, endDate) => {
    // Returns a list of dates between the given start and end date, including the start and end date.
    let now = moment(startDate).clone();
    let dates = [];

    while (now.isSameOrBefore(moment(endDate))) {
        dates.push(now.toDate());
        now.add(1, 'days');
    }
    return dates;
};
</script>

<style lang="scss" scoped>
.modal-card-foot {
    justify-content: flex-end;
}
.date-selection {
    .is-include-validation{
        min-height: 4.5rem;
        margin-bottom: 0;
    }
    .start-date,
    .end-date {
        /deep/ .field-label {
            flex-basis: auto;
            text-align: left;
            flex-grow: 0
        }
        padding-bottom: 0;
    }
    .quick-selections {
        padding-left: 0.6rem;
        padding-top: 3rem;
        display: block;

        button {
            text-decoration: none;
            display: block;
        }
    }

    /deep/ .datepicker {
        .dropdown-menu {
            min-width: 21.4rem;

            .dropdown-content {
                min-height: 22.2rem;

                .dropdown-item {
                    &:focus {
                        outline: none;
                    }

                    .is-selected {
                        border-radius: 0;
                    }

                    .has-event:not(.is-selected) {
                        background-color: $primary-semi-transparent;
                        border-radius: 0;

                        &:hover {
                            background-color: $primary-semi-transparent-hover;
                        }
                    }
                }
            }
        }
    }
}
</style>
